// CategoriesDisplayTwo.js
import React from "react";
import { Link } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import styled from "styled-components";
import "react-lazy-load-image-component/src/effects/blur.css";
import { urlFor } from "../sanity/sanityClient";
import { MdStorefront } from "react-icons/md";
import { useStateContext } from '../context/StateContext';
import { toast } from 'react-hot-toast';
import analytics from '../utils/analytics'; // Import analytics

/* ==========================================================================
   Card design adopted from RecommendedProducts (Facebook Marketplace feel):
   white tile, 10px radius, soft shadow, portrait image (cover), bold price
   on top, muted 2-line name, tight padding. The GRID itself is unchanged.
   ========================================================================== */

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  padding: 4px 6px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #ffffff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  text-align: left;
`;

const StyledLink = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  -webkit-tap-highlight-color: transparent;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1.18;          /* slightly portrait, like the rail cards */
  background: #e4e6eb;
  overflow: hidden;

  /* LazyLoadImage wraps the <img> in a span; make it fill the frame. */
  .cdt-img-wrap {
    display: block !important;
    width: 100%;
    height: 100%;
  }
`;

const StatusSticker = styled.span`
  position: absolute;
  top: 6px;
  left: 6px;
  margin: 0;
  padding: 2px 7px;
  border-radius: 10px;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  pointer-events: none;
  z-index: 2;
  background: ${({ status }) =>
    status === "available" ? "rgba(40,167,69,0.9)" : "rgba(220,53,69,0.9)"};
`;

const StyledImage = styled(LazyLoadImage)`
  display: block;
  width: 100%;
  height: 100%;
  max-width: none;
  object-fit: cover;
  border: 0;
  border-radius: 0;
  z-index: 1;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 5px 8px 7px;
  flex-grow: 1;
`;

const Price = styled.p`
  margin: 0;
  color: #050505;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Name = styled.p`
  margin: 0;
  color: #65676b;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

const Location = styled.p`
  margin: 0;
  color: #65676b;
  font-size: 11px;
  line-height: 1.3;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BuyButton = styled.button`
  width: 100%;
  background: white;
  color: #111;
  border: none;
  padding: 6px;
  margin-top: auto;               /* buttons line up at the bottom of every card */
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: background 0.3s ease;

  &:hover {
    background: #e4e6eb;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const WishlistButton = styled.button`
  width: 100%;
  background: transparent;
  color: #65676b;
  border: 1px solid #dddfe2;
  padding: 4px;
  margin-top: 4px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.3s ease;

  &:hover {
    background: #f0f2f5;
    border-color: #bcc0c4;
  }
`;

const CategoriesDisplayTwo = ({ 
  categories, 
  handleProductView, 
  handleAddToCart,
  nearBy 
}) => {
  const { setShowCart, onAdd, onRemove, cartItems } = useStateContext();

  const getUniqueKey = (item, index) => {
    return item._id ? `${item._id}-${index}` : `fallback-${index}`;
  };

  const getImageUrl = (image) => {
    try {
      return image?.asset?._ref ? urlFor(image).url() : null;
    } catch (error) {
      console.error("Error generating image URL:", error);
      analytics.trackError(error, { context: 'get_image_url' });
      return null;
    }
  };

  const isInCart = (productId) => {
    return cartItems.some(item => item._id === productId);
  };
  
  const handleAddToCartClick = (product, index) => {
    if (!product) {
      toast.error("Product information is missing");
      analytics.trackError(new Error('Product information missing'), { 
        context: 'add_to_cart_click' 
      });
      return;
    }

    // Track add to cart
    analytics.trackAddToCart(product, 1);
    analytics.trackClick('buy_button_grid', { 
      productId: product._id, 
      productName: product.name,
      productPrice: product.price,
      productCurrency: product.currency
    });

    const productForCart = {
      ...product,
      quantity: 1,
      selectedIndex: index
    };

    const result = onAdd(productForCart, 1, index);
    if (result?.success) {
      setShowCart(true);
      analytics.trackClick('cart_icon', { 
        action: 'open_cart_from_product',
        productId: product._id
      });
    }
  };

  const handleRemoveFromCart = (product) => {
    if (!product) return;
    
    analytics.trackRemoveFromCart(product);
    analytics.trackClick('remove_from_cart_button', { 
      productId: product._id, 
      productName: product.name 
    });
    
    onRemove(product);
  };

  const handleWishlistAdd = (product) => {
    analytics.trackWishlistAdd(product);
    analytics.trackClick('wishlist_add', { 
      productId: product._id, 
      productName: product.name 
    });
    
    // Add to localStorage wishlist
    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    if (!wishlist.some(item => item._id === product._id)) {
      wishlist.push({
        _id: product._id,
        name: product.name,
        price: product.price,
        currency: product.currency,
        image: product.image?.[0],
        addedAt: new Date().toISOString()
      });
      localStorage.setItem('wishlist', JSON.stringify(wishlist));
      toast.success('Added to wishlist');
    } else {
      toast('Already in wishlist');
    }
  };

  const handleWishlistRemove = (product) => {
    analytics.trackWishlistRemove(product);
    
    // Remove from localStorage wishlist
    let wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    wishlist = wishlist.filter(item => item._id !== product._id);
    localStorage.setItem('wishlist', JSON.stringify(wishlist));
    toast.success('Removed from wishlist');
  };

  const isInWishlist = (productId) => {
    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    return wishlist.some(item => item._id === productId);
  };

  const handleImageLoad = (product) => {
    analytics.track('product_image_loaded', {
      productId: product._id,
      productName: product.name,
      loadTime: performance.now()
    });
  };

  const handleImageError = (product, error) => {
    analytics.trackError(error, {
      context: 'product_image_load',
      productId: product._id,
      productName: product.name
    });
  };

  const handleProductLinkClick = (product) => {
    // Already handled by handleProductView, but we can add additional tracking
    analytics.trackClick('product_link', {
      productId: product._id,
      productName: product.name,
      position: 'grid'
    });
  };

  return (
    <GridContainer>
      {categories?.map((category, index) => {
        const imageUrl = getImageUrl(category.image?.[0]);
        const inCart = isInCart(category._id);
        const inWishlist = isInWishlist(category._id);
        
        return (
          <Card key={getUniqueKey(category, index)}>
            <StyledLink
              to={`/category/${category.slug?.current}`}
              onClick={() => {
                handleProductView(category._id, category._type);
                handleProductLinkClick(category);
              }}
            >
              <ImageWrapper>
                {category.status && (
                  <StatusSticker status={category.status}>
                    {category.status}
                  </StatusSticker>
                )}

                {imageUrl ? (
                  <StyledImage
                    src={imageUrl}
                    alt={category.name || 'Product image'}
                    effect="blur"
                    wrapperClassName="cdt-img-wrap"
                    draggable={false}
                    onLoad={() => handleImageLoad(category)}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      handleImageError(category, new Error('Image failed to load'));
                    }}
                    afterLoad={() => {
                      analytics.track('product_image_visible', {
                        productId: category._id,
                        productName: category.name,
                        positionInGrid: index
                      });
                    }}
                  />
                ) : (
                  <div 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: '#999',
                      fontSize: '12px'
                    }}
                    onClick={() => analytics.trackClick('no_image_placeholder', { productId: category._id })}
                  >
                    No Image
                  </div>
                )}
              </ImageWrapper>
            </StyledLink>

            <Details>
              {/* Price first, then name: same order as the recommendation rail */}
              <Price
                onClick={() => analytics.trackClick('product_price', { 
                  productId: category._id, 
                  price: category.price,
                  currency: category.currency
                })}
              >
                {category.currency || 'TZS'}: {category.price?.toLocaleString() || '0'}/=
              </Price>

              <Name
                onClick={() => analytics.trackClick('product_name', { 
                  productId: category._id, 
                  productName: category.name 
                })}
              >
                {category.name || 'Unnamed Product'}
              </Name>
              
              {category.location && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '6px',
                    marginTop: '1px'
                  }}
                >
                  <Location
                    onClick={() => analytics.trackClick('product_location', { 
                      productId: category._id, 
                      location: category.location 
                    })}
                  >
                    🌍 {category.location}
                  </Location>

                  <Link
                    style={{
                      textDecoration: 'none',
                      color: '#111',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                    to={`/shop/${category.user}`}
                    onClick={() =>
                      analytics.trackClick('merchant_store_link', {
                        merchantId: category.user,
                        productId: category._id
                      })
                    }
                  >
                    <MdStorefront size={18} />
                  </Link>
                </div>
              )}
              
              <BuyButton 
                onClick={() => {
                  if (inCart) {
                    handleRemoveFromCart(category);
                  } else {
                    handleAddToCartClick(category, index);
                  }
                }}
                disabled={!category.price}
              >
                {inCart ? 'Remove from Cart' : 'Buy Now'}
              </BuyButton>
              
              <WishlistButton
                onClick={() => {
                  if (inWishlist) {
                    handleWishlistRemove(category);
                  } else {
                    handleWishlistAdd(category);
                  }
                }}
              >
                {inWishlist ? '❤️ In Wishlist' : '🤍 Add to Wishlist'}
              </WishlistButton>
              
              {nearBy && category.distance && (
                <Location style={{ fontSize: '10px', marginTop: '3px' }}>
                  📍Estimated {category.distance.toFixed(1)} km away 
                </Location>
              )}
            </Details>
          </Card>
        );
      })}
    </GridContainer>
  );
};

export default CategoriesDisplayTwo;